import { TRIAL_DAYS } from "@gmx/shared";
import { Button, Callout, Field, Input } from "@gmx/ui";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { ApiError } from "../../lib/api";
import { safeNext, useAuth } from "../../lib/auth";
import { AuthHeading, AuthLayout } from "./AuthLayout";
import { PasswordInput } from "./PasswordInput";
import { UpgradeOffer } from './UpgradeOffer';

function loginErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401 || err.code === "invalid_credentials") return "That email and password don't match. Try again.";
    if (err.code === "suspended" || (err.status === 403 && /suspend/i.test(err.message)))
      return err.message || "This account is suspended. Talk to whoever set it up for you.";
    if (err.status === 429) return "Too many attempts. Wait a few minutes, then try again.";
    return err.message;
  }
  return "Something went wrong. Try again.";
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const prefill = (location.state as { email?: string } | null)?.email ?? "";
  const [email, setEmail] = useState(prefill);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [xReady, setXReady] = useState(false);
  useEffect(() => {
    let active = true;
    fetch('/api/auth/x/status', { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .then(value => { if (active) setXReady(value?.configured === true); })
      .catch(() => {});
    return () => { active = false; };
  }, []);
  const [upgradeToken,setUpgradeToken] = useState(()=>new URLSearchParams(location.hash.slice(1)).get('upgrade'));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError("Enter your email and password.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      navigate(safeNext(params.get("next")), { replace: true });
    } catch (err) {
      if(err instanceof ApiError && err.code==='signup_verification_required' && err.signupUrl) {
        const signupUrl=new URL(err.signupUrl,window.location.origin);
        if(signupUrl.origin===window.location.origin && signupUrl.pathname==='/signup') { window.location.assign(signupUrl.href); return; }
      }
      if(err instanceof ApiError && err.code==='upgrade_required' && err.upgradeUrl) {
        const offerUrl=new URL(err.upgradeUrl,window.location.origin);
        if(offerUrl.origin===window.location.origin) {
          setUpgradeToken(new URLSearchParams(offerUrl.hash.slice(1)).get('upgrade'));
        }
      }
      setError(loginErrorMessage(err));
      setBusy(false);
    }
  }

  if(upgradeToken)return <UpgradeOffer token={upgradeToken}/>;
  return (
    <AuthLayout title="Sign in">
      <AuthHeading title="Sign in" lede="Welcome to your Grow My X workspace." />
      <p>Development preview. <a href="https://www.growmyx.com">See what we’re building.</a></p>
      {params.get('x') === 'not_linked' ? <Callout tone="danger">Sign in with email and password first, then open Settings → Profile &amp; password → X login to connect your account.</Callout> : null}
      {params.get('x') === 'cancelled' ? <Callout>X sign-in was cancelled. You can try again or sign in with your password.</Callout> : null}
      {xReady ? <form method="post" action="/api/auth/x/start?mode=login">
        <Button type="submit" variant="primary" size="lg" block>Sign in with X</Button>
      </form> : null}
      <form className="gfp-auth__form" onSubmit={onSubmit} noValidate>
        <Field label="Email">
          <Input
            type="email"
            autoComplete="username"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="lg"
            autoFocus
            placeholder="you@business.com"
          />
        </Field>
        <Field
          label="Password"
          labelAction={
            <Link className="gf-link" to="/forgot" state={{ email }}>
              Forgot password?
            </Link>
          }
        >
          <PasswordInput autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} size="lg" />
        </Field>
        {error ? <Callout tone="danger">{error}</Callout> : null}
        <Button type="submit" variant="primary" size="lg" block loading={busy}>
          Sign in
        </Button>
      </form>
      <p className="gfp-auth__aside">
        New to Grow My X? <Link className="gf-link" to="/signup">Explore Founding Partnerships</Link>.
      </p>
    </AuthLayout>
  );
}
